/**
 * The 3D half of the try band, behind a facade.
 *
 * **The viewer chunk is ~627 KB of three.js.** Loading it on page load would
 * wreck the homepage's LCP for every visitor, including the ones who scroll
 * past without touching anything — and on a phone it is a real slice of
 * somebody's data plan. So nothing is imported until a deliberate press.
 *
 * The facade pattern, as used by `lite-youtube`: a cheap poster with a control
 * where the expensive thing will be. It lands well here because the expensive
 * thing *is* an interaction — the invitation and the trigger are the same
 * button, so no affordance is wasted and nothing is spent on a visitor who
 * never asks.
 *
 * See docs/decisions/homepage.md §9.
 */
import { CANVAS, type RegionId } from "../../shared/format";
import { GARMENT_GROUPS } from "../../shared/parts";
import { createGhostController, type GhostController } from "../viewer/ghost";
import type { ViewerHandle } from "../viewer/types";
import { presetForRegion } from "./camera";
import { drawTemplate } from "./template-file";

export interface BodyPane {
  el: HTMLElement;
  /** Wear this template. Null strips back to bare skin. */
  setArtwork(source: CanvasImageSource | null, size?: { width: number; height: number }): void;
  /** Shirt or pants: changes which parts wear the texture, and the position map. */
  setGarment(next: "shirt" | "pants"): void;
  /** Light the face that samples this region, and turn to look at it. */
  show(region: RegionId | null): void;
  /** Told when the body is tapped, so the map can follow. */
  onPick(fn: (id: RegionId) => void): void;
  /**
   * Load the 3D preview now, without waiting for the facade to be pressed.
   *
   * Dropping a file *is* the request for a preview, so it overrides the
   * narrow-screen gate — nobody drops a template and then wants to be asked
   * whether they meant it. Safe to call repeatedly; already-loading and
   * already-loaded both no-op.
   */
  load(): void;
  destroy(): void;
}

/**
 * `"shirt" | "pants"`, not `GarmentType` — that union also carries `tshirt`,
 * which `GARMENT_GROUPS` has no entry for. Widening the parameter would have
 * made an unmappable garment a runtime `undefined` instead of a type error.
 */
/**
 * Has this person asked for less movement?
 *
 * Checked at call time rather than cached: the setting can change while a page
 * is open, and a cached answer would keep animating for the rest of the session.
 */
function reducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Rig part names are not prose. "LeftArm" becomes "left arm". */
function partLabel(part: string): string {
  return part.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
}

export function createBodyPane(initialGarment: "shirt" | "pants" = "shirt"): BodyPane {
  let garment = initialGarment;
  const root = document.createElement("div");
  root.className = "bpane";

  /*
   * A fixed aspect box, present from the first paint.
   *
   * The canvas mounts long after layout, and a late-arriving element that
   * pushes the page around is the classic way to fail Cumulative Layout Shift —
   * a Core Web Vital, and one this page would otherwise fail on the exact
   * interaction it is built around.
   */
  const stage = document.createElement("div");
  stage.className = "bpane__stage";

  const facade = document.createElement("button");
  facade.type = "button";
  facade.className = "bpane__facade";
  facade.innerHTML =
    '<span class="bpane__cue">Show it on a body</span>' +
    '<small class="bpane__cost">Loads the 3D preview</small>';

  const status = document.createElement("p");
  status.className = "bpane__status";
  status.setAttribute("aria-live", "polite");

  stage.appendChild(facade);
  root.appendChild(stage);
  root.appendChild(status);

  let viewer: ViewerHandle | null = null;
  let loading = false;
  let dead = false;
  let pending: RegionId | null = null;
  /**
   * The template the body wears.
   *
   * Created on demand rather than at mount, and deliberately **not** owned by
   * `load()`: on a narrow screen the viewer waits for a press, so a file
   * dropped first would have been drawn into nothing and silently lost. The
   * canvas outlives the viewer and is bound to it whenever one appears.
   */
  let surface: HTMLCanvasElement | null = null;

  function ensureSurface(): HTMLCanvasElement {
    if (surface) return surface;
    surface = document.createElement("canvas");
    surface.width = CANVAS.width;
    surface.height = CANVAS.height;
    return surface;
  }
  /** Fades whatever stands in front of the selected face. Shared with the editor. */
  let ghost: GhostController | null = null;
  const pickListeners = new Set<(id: RegionId) => void>();

  async function load() {
    if (viewer || loading || dead) return;
    loading = true;
    facade.disabled = true;
    // Replaces the invitation, which is wrong the moment it is already
    // happening — and on the auto path nobody pressed anything.
    facade.innerHTML = '<span class="bpane__cue">Loading the 3D preview…</span>';
    status.textContent = "";

    try {
      const { webglSupported } = await import("../viewer/support");
      if (!webglSupported()) {
        /*
         * Not an error, and not a dead end: the map beside this is the whole
         * feature minus the body, so say what is missing rather than what
         * failed.
         */
        status.textContent = "This device can't run the 3D preview. The map still works.";
        facade.remove();
        return;
      }

      const { mountViewer } = await import("../viewer/index");
      const host = document.createElement("div");
      host.className = "bpane__canvas";
      stage.replaceChildren(host);

      viewer = await mountViewer(host, {
        body: "r6-classic",
        mode: "flat",
        /*
         * Harder than the editor's default of 1, but not by as much as it was.
         *
         * 2.2 was tuned against bare skin, where the tint is the only thing to
         * see. With a template loaded it washed the artwork out — covering the
         * very thing the highlight is pointing at. The 2D map's glowing outline
         * carries most of the "this face" job now, so the body only has to
         * agree with it rather than shout.
         */
        highlightStrength: 1.5,
        // Double-click, per the viewer's contract. A bonus for pointers; the
        // map is the primary control and works everywhere.
        onFacePick: ({ region }) => {
          for (const fn of pickListeners) fn(region);
        },
      });

      /*
       * Bind a blank template to every group, or the highlight does nothing.
       *
       * Not decoration. `createMaterial` only calls `injectHighlight` when the
       * material has a map — its own comment: "vMapUv only exists in the
       * compiled shader when the material has a map, so the highlight is only
       * injected where it can compile." With no surface bound the body renders
       * as plain skin, `highlightRegion()` sets a uniform nothing reads, and
       * the feature silently does nothing. Verified by screenshot, not assumed.
       *
       * Transparent texels still render as the body colour, so a blank surface
       * looks exactly like bare skin — and this is the same canvas a dropped
       * PNG will be drawn into.
       */
      /*
       * Bind whatever surface exists — including one already carrying artwork
       * that was dropped before this loaded. A blank one is created if there
       * is none, because `highlightRegion` is a no-op on a material with no
       * map at all.
       */
      ensureSurface();
      bindSurfaces();

      // The controller is the shared policy — see src/viewer/ghost.ts. It
      // honours prefers-reduced-motion on its own.
      ghost = createGhostController(viewer);

      status.textContent = "";
      // A region chosen while this was still loading must not be lost.
      if (pending) show(pending);
    } catch {
      status.textContent = "The 3D preview didn't load. The map still works.";
      facade.disabled = false;
    } finally {
      loading = false;
    }
  }

  facade.addEventListener("click", () => void load());

  /**
   * Should this load itself, or wait to be asked?
   *
   * The facade exists because 627 KB must not touch first paint. But on a wide
   * screen it turns the best thing on the page into a button most people will
   * never press — the body simply is not there, which is what it looked like.
   *
   * So: load it unasked where the cost is least likely to matter, and keep the
   * press everywhere it might. Both gates are deliberate.
   *
   *   - `Save-Data` / reduced data is somebody explicitly asking for less.
   *   - Under the paired breakpoint the layout is stacked, which is a phone or
   *     a narrow window, where 627 KB is a real charge on a real plan.
   *
   * Either way it is after first paint, because it waits for the band to be
   * scrolled into view.
   */
  function mayAutoLoad(): boolean {
    const conn = (navigator as { connection?: { saveData?: boolean } }).connection;
    if (conn?.saveData) return false;
    if (window.matchMedia("(prefers-reduced-data: reduce)").matches) return false;
    // 60rem, the same breakpoint that pairs the two panes.
    return window.matchMedia("(min-width: 60rem)").matches;
  }

  /*
   * Intersection, not load: nothing here may compete with the hero for the
   * network while the page is still painting.
   */
  const seen = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      seen.disconnect();
      if (mayAutoLoad()) void load();
    },
    { rootMargin: "200px" },
  );
  seen.observe(root);

  /*
   * Bind the template to the groups this garment covers, and clear the rest.
   *
   * Clearing matters as much as binding. `main.ts:bindSurfaces` learned this
   * the hard way: a group keeps rendering the last texture uploaded to it, so
   * switching shirt to pants leaves the arms still wearing the shirt over a
   * template that no longer contains it.
   *
   * Also why the highlight only lit the right limb: any group carrying a map
   * samples the same highlight rect, so a shirt must not dress legs.
   */
  function bindSurfaces() {
    if (!viewer || !surface) return;
    const covered = new Set(GARMENT_GROUPS[garment]);
    for (const group of ["torso", "arms", "legs"] as const) {
      viewer.setSurface(group, covered.has(group) ? surface : null);
      if (covered.has(group)) viewer.invalidate(group);
    }
  }

  function show(region: RegionId | null) {
    if (!viewer) {
      // Remember it, so pressing the button after choosing a region arrives
      // already looking at the right face.
      pending = region;
      return;
    }
    viewer.highlightRegion(region);
    /*
     * Ghost first, then turn. Six of eighteen shirt faces sit behind something,
     * and lighting one of those without moving the blocker looks exactly like
     * the highlight being broken. Both share the 320ms window, so the limb is
     * out of the way as the camera arrives.
     */
    ghost?.reveal(region);
    /*
     * Say why a limb faded.
     *
     * A body part quietly going translucent is a strange thing to do to
     * somebody without explanation — and this doubles as the only way to
     * observe the ghost state from outside, which is how a wiring bug in it
     * gets caught rather than guessed at.
     */
    const hidden = ghost?.hidden() ?? [];
    status.textContent = hidden.length
      ? `${hidden.map(partLabel).join(" and ")} faded to show this face.`
      : "";
    /*
     * Swept, not snapped — the turn is the moment this stops being a diagram,
     * and a click that teleports the camera reads as the view breaking.
     *
     * Except when motion is unwelcome. The ghost controller and the viewer's
     * own body cross-fade both honour `prefers-reduced-motion`; this was the
     * one path that did not, so selecting a face swung the camera for somebody
     * who had asked for none. The face still gets shown, it just arrives.
     */
    if (region) viewer.frame(presetForRegion(region), { animate: !reducedMotion() });
  }

  return {
    el: root,
    show,
    load: () => void load(),

    setArtwork(source, size) {
      const canvas = ensureSurface();
      if (!source) {
        canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
      } else {
        drawTemplate(canvas, source, size ?? { width: canvas.width, height: canvas.height });
      }
      // The canvas is bound by reference, so the viewer needs telling that the
      // pixels under it moved — it does not poll.
      for (const group of GARMENT_GROUPS[garment]) viewer?.invalidate(group);
    },

    setGarment(next) {
      if (next === garment) return;
      garment = next;
      // The viewer keeps one position map per garment; without this, a point on
      // the torso is ambiguous and every occluder answer is the other garment's.
      viewer?.setGarment(next);
      bindSurfaces();
      // Occluders differ per garment — a sleeve's inner face is blocked by the
      // torso, a trouser leg's only by the other leg — so a stale ghost would
      // be hiding the wrong limb.
      ghost?.clear();
    },
    onPick(fn) {
      pickListeners.add(fn);
    },
    destroy() {
      dead = true;
      seen.disconnect();
      pickListeners.clear();
      // `dispose`, which is what ViewerHandle actually exposes — there is no
      // `destroy`. Optional-chaining a method that does not exist would have
      // failed silently and leaked a WebGL context per mount.
      viewer?.dispose();
      viewer = null;
      root.remove();
    },
  };
}
